/**
 * WorkspaceSummaryPanel — workspace-level executive summary
 *
 * Combines:
 *  1. Hero photo + presenting concern / summary findings (same layout as overview tab)
 *  2. Cumulative AI-generated summaries from each phase's sessions
 *  3. Sticky status bar at the bottom (phase, category, date, team, evidence, solutions)
 *
 * Summary extraction uses multi-key/recursive JSON parsing to handle
 * AI response format variation (same approach as EvidenceTracker).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { AiSessionResponse } from "../../api/workspaceAi";
import {
  WorkspaceResponse,
  WORKSPACE_PHASES,
  updateWorkspace,
  fetchTeamMembers,
  TeamMemberResponse,
} from "../../api/workspaces";
import {
  fetchAllSecurityUsers,
  SecurityUserResponse,
} from "../../api/security";
import { uploadFile } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./WorkspaceSummaryPanel.module.css";

interface Props {
  workspace: WorkspaceResponse;
  sessions: AiSessionResponse[];
  onOpenTeamModal?: () => void;
  /** Called after the workspace is mutated (e.g. hero photo changed) */
  onWorkspaceUpdated?: (updated: WorkspaceResponse) => void;
}

/* ── Section SVG icons — 16×16, stroke-based, coloured via CSS ── */
const ICON_SIZE = 16;
const ICON_PROPS = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Alert triangle — Presenting Concern */
const IconPresentingConcern = () => (
  <svg {...ICON_PROPS}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/** Clipboard with list — Current Findings */
const IconCurrentFindings = () => (
  <svg {...ICON_PROPS}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

/** Magnifying glass over bar-chart — Initial Analysis */
const IconInitialAnalysis = () => (
  <svg {...ICON_PROPS}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="14" x2="8" y2="10" />
    <line x1="11" y1="14" x2="11" y2="8" />
    <line x1="14" y1="14" x2="14" y2="11" />
  </svg>
);

/** Folder archive — Evidence Collection Plan */
const IconEvidencePlan = () => (
  <svg {...ICON_PROPS}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <line x1="9" y1="14" x2="15" y2="14" />
  </svg>
);

/** Crosshair target — Root Cause Analysis */
const IconRootCause = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </svg>
);

/** Lightbulb — Solution Design */
const IconSolutionDesign = () => (
  <svg {...ICON_PROPS}>
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
  </svg>
);

/** Map session type keys → icon components */
const SECTION_ICONS: Record<string, React.FC> = {
  initial_analysis: IconInitialAnalysis,
  evidence_plan: IconEvidencePlan,
  analysis: IconRootCause,
  solution_design: IconSolutionDesign,
};

/** Initial analysis is rendered separately in the team+analysis row */
const INITIAL_ANALYSIS_SECTION = {
  sessionType: "initial_analysis",
  phase: "detection_validation",
  i18nKey: "initialAnalysis",
} as const;

/** Remaining session types rendered as stacked cards below */
const SUMMARY_SECTIONS = [
  {
    sessionType: "evidence_plan",
    phase: "detection_validation",
    i18nKey: "evidencePlan",
  },
  {
    sessionType: "analysis",
    phase: "analysis",
    i18nKey: "analysis",
  },
  {
    sessionType: "solution_design",
    phase: "solution_design",
    i18nKey: "solutionDesign",
  },
] as const;

/** Candidate keys the AI might use for the executive summary field */
const SUMMARY_KEYS = [
  "plan_summary",
  "planSummary",
  "executive_summary",
  "executiveSummary",
  "summary",
  "overview",
];

/**
 * Recursively search a parsed JSON object for a summary string value.
 * Tries each candidate key at every nesting level.
 */
function findSummaryText(obj: unknown): string | null {
  if (!obj || typeof obj !== "object") return null;
  const record = obj as Record<string, unknown>;

  for (const key of SUMMARY_KEYS) {
    const val = record[key];
    if (typeof val === "string" && val.trim().length > 20) {
      return val.trim();
    }
  }

  for (const val of Object.values(record)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const found = findSummaryText(val);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Try to parse JSON from raw text, handling markdown code fences.
 */
function tryParseJson(text: string): unknown | null {
  if (!text) return null;

  let cleaned = text.replace(/```(?:json)?\s*/gi, "").replace(/```/g, "");
  cleaned = cleaned.trim();

  try {
    return JSON.parse(cleaned);
  } catch {
    // Fall through
  }

  const start = cleaned.indexOf("{");
  if (start === -1) return null;

  let depth = 0;
  for (let i = start; i < cleaned.length; i++) {
    if (cleaned[i] === "{") depth++;
    else if (cleaned[i] === "}") {
      depth--;
      if (depth === 0) {
        try {
          return JSON.parse(cleaned.slice(start, i + 1));
        } catch {
          return null;
        }
      }
    }
  }

  return null;
}

function extractSummary(session: AiSessionResponse): string | null {
  for (const field of [session.outputPayload, session.rawOutput]) {
    if (!field) continue;
    const parsed = tryParseJson(field);
    if (parsed) {
      const summary = findSummaryText(parsed);
      if (summary) return summary;
    }
  }
  return null;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

/** Split a display name into first-name and last-name rows */
function splitName(name: string): { first: string; last: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return { first: parts[0] || "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const WorkspaceSummaryPanel: React.FC<Props> = ({
  workspace,
  sessions,
  onOpenTeamModal,
  onWorkspaceUpdated,
}) => {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const heroFileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // ── Team roster data ──
  const [teamMembers, setTeamMembers] = useState<TeamMemberResponse[]>([]);
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTeamLoading(true);
      try {
        const [members, users] = await Promise.all([
          fetchTeamMembers(workspace.workspaceGuid),
          fetchAllSecurityUsers(),
        ]);
        if (!cancelled) {
          setTeamMembers(members);
          setAllUsers(users.filter((u) => u.isActive));
        }
      } catch {
        // silent — panel will show placeholder
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspace.workspaceGuid, workspace.teamMemberCount]);

  const userMap = useMemo(() => {
    const map = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
    return map;
  }, [allUsers]);

  const getUser = useCallback(
    (guid: string): SecurityUserResponse | undefined =>
      userMap.get(guid.toUpperCase()),
    [userMap]
  );

  const leadUser = useMemo(() => getUser(workspace.teamLeadGuid), [getUser, workspace.teamLeadGuid]);

  // Hero image resolution
  const heroKey = workspace.detailHeroImageUrl ?? workspace.heroImageUrl;
  const heroUrl = mediaSrc(heroKey);

  /** Trigger the hidden file input */
  const handleChangePhoto = useCallback(() => {
    heroFileRef.current?.click();
  }, []);

  /** Upload selected file → update workspace → notify parent */
  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const result = await uploadFile(
          file,
          "heroes",
          workspace.workspaceGuid,
          file.name
        );
        const updated = await updateWorkspace(workspace.workspaceGuid, {
          detailHeroImageUrl: result.key,
        });
        onWorkspaceUpdated?.(updated);
      } catch (err) {
        console.error("Hero photo upload failed:", err);
      } finally {
        setUploading(false);
        // Reset so selecting the same file again triggers onChange
        if (heroFileRef.current) heroFileRef.current.value = "";
      }
    },
    [workspace.workspaceGuid, onWorkspaceUpdated]
  );

  /** Print the executive summary */
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const createdDate = new Date(workspace.createdAt).toLocaleDateString(
    undefined,
    { year: "numeric", month: "short", day: "numeric" }
  );

  const phaseLabel = (phase: string): string =>
    t(`workspace.phaseLabels.${phase}`) || phase;

  /** Helper: resolve summary data for a given section config */
  const resolveSectionData = useMemo(() => {
    return (section: { sessionType: string; phase: string; i18nKey: string }) => {
      const matching = sessions
        .filter(
          (s) =>
            s.sessionType === section.sessionType &&
            (s.status === "completed" || s.status === "reviewed")
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

      const latestSession = matching[0] ?? null;
      const summaryText = latestSession
        ? extractSummary(latestSession)
        : null;

      return {
        ...section,
        summaryText,
        sessionDate: latestSession?.createdAt ?? null,
        hasSession: matching.length > 0,
      };
    };
  }, [sessions]);

  /** Initial analysis — rendered in the team+analysis row */
  const initialAnalysis = useMemo(
    () => resolveSectionData(INITIAL_ANALYSIS_SECTION),
    [resolveSectionData]
  );

  /** Remaining sections — stacked cards */
  const summaryData = useMemo(
    () => SUMMARY_SECTIONS.map(resolveSectionData),
    [resolveSectionData]
  );

  return (
    <div className={styles.container}>
      {/* Hidden file input for hero photo upload */}
      <input
        ref={heroFileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {/* ── Scrollable content area ── */}
      <div className={styles.scrollArea}>
        {/* Hero photo + Description/Findings — same layout as overview tab */}
        <div className={styles.heroDescRow}>
          {/* Left: Hero photo */}
          <div className={styles.heroSection}>
            {heroUrl ? (
              <>
                <img
                  className={styles.heroImage}
                  src={heroUrl}
                  alt={workspace.presentingConcern}
                />
                {uploading && (
                  <div className={styles.heroUploading}>
                    {t("workspace.summary.uploading")}
                  </div>
                )}
              </>
            ) : (
              <div
                className={styles.heroPlaceholder}
                onClick={handleChangePhoto}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleChangePhoto()}
              >
                <span>📷</span>
                <span>{uploading ? t("workspace.summary.uploading") : t("workspace.changePhoto")}</span>
              </div>
            )}
            {heroUrl && (
              <button
                type="button"
                className={styles.heroEditBtn}
                onClick={handleChangePhoto}
                disabled={uploading}
              >
                {uploading ? t("workspace.summary.uploading") : t("workspace.changePhoto")}
              </button>
            )}
          </div>

          {/* Right: Description + Findings stacked */}
          <div className={styles.descriptionStack}>
            <div className={styles.descriptionCard}>
              <span className={styles.descriptionCardLabel}>
                <IconPresentingConcern />
                {t("workspace.detail.presentingConcern")}
              </span>
              <div className={styles.descriptionCardBody}>
                {workspace.presentingConcernValidatedAt
                  ? workspace.presentingConcern
                  : workspace.description || workspace.presentingConcern}
              </div>
            </div>
            {workspace.summaryFindings && (
              <div className={styles.descriptionCard}>
                <span className={styles.descriptionCardLabel}>
                  <IconCurrentFindings />
                  {t("workspace.detail.summaryFindings")}
                  <span className={styles.aiBadge}>AI + Human</span>
                </span>
                <div className={styles.descriptionCardBody}>
                  {workspace.summaryFindings}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Team + Initial Analysis — side-by-side row ── */}
        <div className={styles.teamAnalysisRow}>
          {/* Left: Team panel placeholder */}
          <div className={styles.teamPanel}>
            <span className={styles.teamPanelLabel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {t("workspace.summary.teamTitle")}
              <span className={styles.teamRosterCount}>
                {teamMembers.length + 1}
              </span>
            </span>

            {teamLoading ? (
              <div className={styles.teamPlaceholderBody}>
                <span className={styles.teamPlaceholderHint}>
                  {t("workspace.team.loading")}
                </span>
              </div>
            ) : (
              <div className={styles.teamRosterScroll}>
                {/* Team lead — always first */}
                <button
                  type="button"
                  className={styles.teamChip}
                  onClick={() => leadUser && navigate(`/profile/${leadUser.userGuid}`)}
                  title={leadUser?.displayName || leadUser?.name || ""}
                >
                  <div className={styles.teamChipAvatar}>
                    {leadUser && profileImgUrl(leadUser) ? (
                      <img
                        src={profileImgUrl(leadUser)!}
                        alt=""
                        className={styles.teamChipAvatarImg}
                      />
                    ) : (
                      <span className={styles.teamChipInitials}>
                        {leadUser
                          ? getInitials(leadUser.displayName || leadUser.name)
                          : "??"}
                      </span>
                    )}
                  </div>
                  <span className={styles.teamChipName}>
                    {(() => {
                      const n = splitName(leadUser?.displayName || leadUser?.name || t("workspace.team.unknownUser"));
                      return (
                        <>
                          <span className={styles.teamChipFirstName}>{n.first}</span>
                          {n.last && <span className={styles.teamChipLastName}>{n.last}</span>}
                        </>
                      );
                    })()}
                  </span>
                  <span className={styles.teamChipRole}>
                    {t("workspace.team.roleLead")}
                  </span>
                </button>

                {/* Regular members */}
                {teamMembers.map((member) => {
                  const user = getUser(member.userGuid);
                  const img = user ? profileImgUrl(user) : null;
                  return (
                    <button
                      key={member.id}
                      type="button"
                      className={styles.teamChip}
                      onClick={() => navigate(`/profile/${member.userGuid}`)}
                      title={user?.displayName || user?.name || ""}
                    >
                      <div className={styles.teamChipAvatar}>
                        {img ? (
                          <img
                            src={img}
                            alt=""
                            className={styles.teamChipAvatarImg}
                          />
                        ) : (
                          <span className={styles.teamChipInitials}>
                            {user
                              ? getInitials(user.displayName || user.name)
                              : "??"}
                          </span>
                        )}
                      </div>
                      <span className={styles.teamChipName}>
                        {(() => {
                          const n = splitName(user?.displayName || user?.name || member.userGuid);
                          return (
                            <>
                              <span className={styles.teamChipFirstName}>{n.first}</span>
                              {n.last && <span className={styles.teamChipLastName}>{n.last}</span>}
                            </>
                          );
                        })()}
                      </span>
                      <span className={styles.teamChipRole}>
                        {t(`workspace.team.role_${member.role}`)}
                      </span>
                    </button>
                  );
                })}

                {/* Empty state if no additional members */}
                {teamMembers.length === 0 && (
                  <span className={styles.teamRosterHint}>
                    {t("workspace.summary.teamAddHint")}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right: Initial Analysis summary */}
          <div
            className={`${styles.section} ${initialAnalysis.summaryText ? styles.sectionAvailable : styles.sectionPending}`}
          >
            <div className={styles.sectionHeader}>
              <span className={styles.sectionPhase}>
                <IconInitialAnalysis />
                {t(`workspace.summary.sections.${initialAnalysis.i18nKey}`)}
              </span>
              {initialAnalysis.sessionDate && (
                <span className={styles.sectionDate}>
                  {new Date(initialAnalysis.sessionDate).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>
            {(workspace.initialAnalysis || initialAnalysis.summaryText) ? (
              <div className={styles.sectionBody}>{workspace.initialAnalysis || initialAnalysis.summaryText}</div>
            ) : initialAnalysis.hasSession ? (
              <div className={styles.sectionEmpty}>
                <span className={styles.sectionEmptyIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </span>
                {t("workspace.summary.noSummaryInSession")}
              </div>
            ) : (
              <div className={styles.sectionEmpty}>
                <span className={styles.sectionEmptyIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </span>
                {t("workspace.summary.pendingPhase")}
              </div>
            )}
          </div>
        </div>

        <div className={styles.sections}>
          {summaryData.map((section) => (
            <div
              key={section.sessionType}
              className={`${styles.section} ${section.summaryText ? styles.sectionAvailable : styles.sectionPending}`}
            >
              <div className={styles.sectionHeader}>
                <span className={styles.sectionPhase}>
                  {(() => {
                    const Icon = SECTION_ICONS[section.sessionType];
                    return Icon ? <Icon /> : null;
                  })()}
                  {t(`workspace.summary.sections.${section.i18nKey}`)}
                </span>
                {section.sessionDate && (
                  <span className={styles.sectionDate}>
                    {new Date(section.sessionDate).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>

              {section.summaryText ? (
                <div className={styles.sectionBody}>{section.summaryText}</div>
              ) : section.hasSession ? (
                <div className={styles.sectionEmpty}>
                  <span className={styles.sectionEmptyIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </span>
                  {t("workspace.summary.noSummaryInSession")}
                </div>
              ) : (
                <div className={styles.sectionEmpty}>
                  <span className={styles.sectionEmptyIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </span>
                  {t("workspace.summary.pendingPhase")}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Guidance note */}
        <div className={styles.guidance}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          {t("workspace.summary.guidance")}
        </div>

        {/* Print-only branded footer — hidden on screen */}
        <div className={styles.printFooter}>
          {t("workspace.summary.printedFrom", {
            date: new Date().toLocaleString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}
        </div>
      </div>

      {/* ── Sticky status bar — pinned to bottom ── */}
      <div className={styles.statusBar}>
        <span className={styles.statusChip}>
          <span className={styles.chipIcon}>🔴</span>
          {phaseLabel(workspace.phase)}
        </span>
        <span className={styles.statusDivider} />
        <span className={styles.statusChip}>
          <span className={styles.chipIcon}>🏷</span>
          {workspace.concernCategory
            ? t(`workspace.categoryLabels.${workspace.concernCategory}`) || workspace.concernCategory
            : "—"}
        </span>
        <span className={styles.statusDivider} />
        <span className={styles.statusChip}>
          <span className={styles.chipIcon}>📅</span>
          {createdDate}
        </span>
        <span className={styles.statusDivider} />
        {onOpenTeamModal ? (
          <span
            className={styles.statusChipClickable}
            role="button"
            tabIndex={0}
            onClick={onOpenTeamModal}
            onKeyDown={(e) => e.key === "Enter" && onOpenTeamModal()}
          >
            <span className={styles.chipIcon}>👥</span>
            {t("workspace.teamMembers", { count: workspace.teamMemberCount })}
          </span>
        ) : (
          <span className={styles.statusChip}>
            <span className={styles.chipIcon}>👥</span>
            {t("workspace.teamMembers", { count: workspace.teamMemberCount })}
          </span>
        )}
        <span className={styles.statusDivider} />
        <span className={styles.statusChip}>
          <span className={styles.chipIcon}>📎</span>
          {t("workspace.evidenceCount", { count: workspace.evidenceCount })}
        </span>
        <span className={styles.statusDivider} />
        <span className={styles.statusChip}>
          <span className={styles.chipIcon}>💡</span>
          {t("workspace.solutionCount", { count: workspace.solutionCount })}
        </span>
      </div>
    </div>
  );
};

export default WorkspaceSummaryPanel;
